import React, { useEffect, useState } from "react";
import { X, Layers, Bookmark } from "lucide-react";
import { fetchVerseGreek, listBookmarks, createBookmark, deleteBookmarksByRefs } from "@/lib/api";

/**
 * variant: "sheet" (mobile/tablet bottom sheet) | "panel" (desktop right side)
 */
export default function VerseSheet({ chapter, verse, onClose, onOpenWord, variant = "sheet" }) {
    const [data, setData] = useState(null);
    const [interlinear, setInterlinear] = useState(false);
    const [loading, setLoading] = useState(true);
    const [bookmarked, setBookmarked] = useState(false);
    const [saving, setSaving] = useState(false);

    const ref = `Mat.${chapter}.${verse}`;

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setData(null);
        fetchVerseGreek(chapter, verse)
            .then(d => { if (alive) setData(d); })
            .finally(() => { if (alive) setLoading(false); });
        listBookmarks().then(d => {
            if (!alive) return;
            const hit = (d.bookmarks || []).some(b => Array.isArray(b.refs) && b.refs.length === 1 && b.refs[0] === ref);
            setBookmarked(hit);
        });
        return () => { alive = false; };
    }, [chapter, verse, ref]);

    const toggleBookmark = async () => {
        if (saving) return;
        setSaving(true);
        try {
            if (bookmarked) {
                await deleteBookmarksByRefs([ref]);
                setBookmarked(false);
            } else {
                await createBookmark([ref]);
                setBookmarked(true);
            }
        } finally {
            setSaving(false);
        }
    };

    const isPanel = variant === "panel";

    const containerCls = isPanel
        ? "flex flex-col h-full"
        : "absolute inset-x-0 bottom-0 max-w-md md:max-w-lg mx-auto bg-white rounded-t-3xl shadow-[0_-8px_32px_rgba(30,43,38,0.18)] animate-sheet-in flex flex-col max-h-[85vh]";

    const body = (
        <>
            {!isPanel && (
                <div className="pt-2 pb-1 flex justify-center">
                    <div className="w-10 h-1.5 rounded-full bg-ink-10" />
                </div>
            )}

            <div className={"px-6 py-3 border-b border-ink-10 flex items-start justify-between gap-3 " + (isPanel ? "pt-6" : "")}>
                <div className="min-w-0">
                    <div className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep">
                        Matthew {chapter}:{verse}
                    </div>
                    {data?.text_kjv || data?.text_web ? (
                        <p className="scripture text-[15px] leading-snug text-ink mt-1 line-clamp-3">
                            {data.text_kjv || data.text_web}
                        </p>
                    ) : null}
                </div>
                <div className="shrink-0 flex items-center gap-1">
                    <button
                        data-testid="toggle-bookmark"
                        onClick={toggleBookmark}
                        disabled={saving}
                        aria-pressed={bookmarked}
                        aria-label={bookmarked ? "Remove bookmark" : "Bookmark this verse"}
                        title={bookmarked ? "Bookmarked — tap to remove" : "Bookmark this verse"}
                        className={
                            "w-9 h-9 rounded-full flex items-center justify-center transition " +
                            (bookmarked
                                ? "text-moss-deep hover:bg-moss/10"
                                : "text-ink-40 hover:text-moss-deep hover:bg-moss/10")
                        }
                    >
                        <Bookmark
                            size={17}
                            fill={bookmarked ? "currentColor" : "none"}
                            strokeWidth={2}
                        />
                    </button>
                    <button
                        data-testid="close-verse-sheet"
                        onClick={onClose}
                        className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-ink-10 transition"
                        aria-label="Close verse sheet"
                    >
                        <X size={18} className="text-ink" />
                    </button>
                </div>
            </div>

            <div className="px-6 py-3 border-b border-ink-10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Layers size={14} className="text-ink-70" />
                    <span className="font-ui text-[13px] text-ink-70">Greek behind this verse</span>
                </div>
                <button
                    data-testid="interlinear-toggle"
                    onClick={() => setInterlinear(x => !x)}
                    role="switch"
                    aria-checked={interlinear}
                    className={
                        "font-ui text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full transition " +
                        (interlinear
                            ? "bg-moss text-white hover:bg-moss-deep"
                            : "bg-sage-paper text-ink-70 hover:text-ink hover:bg-ink-10")
                    }
                >
                    Interlinear {interlinear ? "on" : "off"}
                </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto sheet-scroll flex-1">
                {loading && (
                    <p className="font-ui text-sm text-ink-40 py-6">Loading Greek…</p>
                )}
                {!loading && data?.tokens?.length === 0 && (
                    <p className="font-ui text-sm text-ink-40 py-6">
                        No Greek tokens available for this verse.
                    </p>
                )}
                {!loading && data?.tokens?.length > 0 && (
                    interlinear
                        ? <InterlinearView tokens={data.tokens} onOpenWord={onOpenWord} isPanel={isPanel} />
                        : <CardList tokens={data.tokens} onOpenWord={onOpenWord} />
                )}
            </div>
        </>
    );

    if (isPanel) {
        return <div data-testid="verse-sheet" className={containerCls}>{body}</div>;
    }

    return (
        <div className="fixed inset-0 z-50 animate-fade-in lg:hidden" data-testid="verse-sheet">
            <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
            <div className={containerCls}>{body}</div>
        </div>
    );
}

function CardList({ tokens, onOpenWord }) {
    return (
        <ul className="flex flex-col gap-2.5 pb-6">
            {tokens.map((t) => (
                <li key={t.position}>
                    <button
                        data-testid={`word-card-${t.position}`}
                        onClick={() => onOpenWord(t)}
                        className="w-full text-left p-4 rounded-2xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-md active:scale-[0.99] transition-all"
                    >
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="greek text-2xl text-ink leading-none">{t.greek}</span>
                            <span className="font-ui text-[11px] uppercase tracking-[0.18em] text-moss-deep shrink-0">
                                {t.strongs}
                            </span>
                        </div>
                        <div className="mt-1.5 font-ui text-[13px] text-ink-70 italic">{t.translit}</div>
                        {t.english && (
                            <div className="mt-1 font-ui text-[13px] text-ink">{t.english}</div>
                        )}
                    </button>
                </li>
            ))}
        </ul>
    );
}

function InterlinearView({ tokens, onOpenWord, isPanel }) {
    // Desktop panel can use even wider spacing; tablet+ shows more columns
    const gap = isPanel ? "gap-x-5 gap-y-6" : "gap-x-4 gap-y-5 md:gap-x-5";
    return (
        <div data-testid="interlinear-view" className={"flex flex-wrap pb-6 " + gap}>
            {tokens.map((t) => (
                <button
                    key={t.position}
                    data-testid={`interlinear-word-${t.position}`}
                    onClick={() => onOpenWord(t)}
                    className="text-left flex flex-col min-w-[64px] md:min-w-[80px] group"
                >
                    <span className="greek text-xl md:text-2xl text-ink leading-tight group-hover:text-moss-deep transition-colors">
                        {t.greek}
                    </span>
                    <span className="font-ui text-[11px] md:text-[12px] text-ink-70 italic mt-0.5">
                        {t.translit}
                    </span>
                    <span className="font-ui text-[11px] md:text-[12px] text-moss-deep mt-0.5">
                        {t.gloss || t.english}
                    </span>
                </button>
            ))}
        </div>
    );
}
